/** @file Sets a slot's setup tint (color and/or two-color dark); omitted fields keep their value. */
import { z } from 'zod';
import { skeletonParts } from '@pose2d/format/doc/schemaParts/skeleton';
import { defineCommand } from '../defineCommand';
import { defineSetter } from '../_shared/defineSetter';

const tint = skeletonParts.slot.shape.setup.pick({ color: true, dark: true }).partial().strict();
const tintSetter = defineSetter({
  domain: 'slot',
  kind: 'slot',
  field: 'setup',
  schema: tint,
  description: 'Set a slot setup tint: value = { color?: "#rrggbbaa", dark?: "#rrggbb" | null } (dark is the two-color tint, null turns it off); omitted fields keep their value.',
});

/** slot.setColor: merges { color?, dark? } into the setup; a value the setup already has is a no-op (R2-13). */
export const command = defineCommand({
  ...tintSetter,
  name: 'slot.setColor',
  input: z.object({ target: z.string().min(1), value: tint }).strict(),
  apply(draft, input, ctx) {
    const setup = draft.slots[ctx.resolve('slot', input.target)]?.setup;
    const { color = setup?.color, dark = setup?.dark } = input.value;
    if (color === setup?.color && dark === setup?.dark) return;
    tintSetter.apply(draft, input, ctx);
  },
});
